// Rekenwerk van de postproductie-wizard (TaskTemplateGenerator) en de
// Planning Puzzel. Eén bron, en een spiegel van de engine
// (ai-engine/api/frontdoor/tasks.py: verdeel_dagen, feedback_nodig, leg_neer):
// liep het voorbeeld uiteen met de engine, dan zag je in de puzzel een andere
// planning dan de taken die uiteindelijk ontstonden.

import { formatDateISO, parseISODate } from './weekUtils';
import { isActieveDraaidag } from './draaidagenPlanning';

const isWerkdag = (d) => d.getDay() !== 0 && d.getDay() !== 6;

/** Alle werkdagen van start t/m eind (inclusief), als YYYY-MM-DD. */
export function werkdagenLijst(startIso, eindIso) {
  const start = parseISODate(String(startIso || '').slice(0, 10));
  const eind = parseISODate(String(eindIso || '').slice(0, 10));
  if (!start || !eind) return [];
  const uit = [];
  for (let d = new Date(start); d <= eind; d.setDate(d.getDate() + 1)) {
    if (isWerkdag(d)) uit.push(formatDateISO(d));
  }
  return uit;
}

export const telWerkdagen = (startIso, eindIso) => werkdagenLijst(startIso, eindIso).length;

/** `n` werkdagen na `iso` (0 = dezelfde dag, weekend schuift door naar maandag). */
export function plusWerkdagen(iso, n) {
  const d = parseISODate(String(iso || '').slice(0, 10));
  if (!d) return null;
  while (!isWerkdag(d)) d.setDate(d.getDate() + 1);
  let rest = Math.max(0, n);
  while (rest > 0) {
    d.setDate(d.getDate() + 1);
    if (isWerkdag(d)) rest -= 1;
  }
  return formatDateISO(d);
}

/** De eerste werkdag ná `iso`. */
export function volgendeWerkdag(iso) {
  const d = parseISODate(String(iso || '').slice(0, 10));
  if (!d) return null;
  do { d.setDate(d.getDate() + 1); } while (!isWerkdag(d));
  return formatDateISO(d);
}

/**
 * Werkdagen naar gewicht verdelen: elke taak minstens één dag en samen precies
 * `budget` (of het aantal taken als dat meer is). Largest remainder — per taak
 * afronden gaf bij drie taken in drie dagen 2+1+1 = vier dagen, en dan liep de
 * planning al over de deadline vóór je iets had gedaan.
 */
export function verdeelDagen(gewichten, budget) {
  const n = gewichten.length;
  if (!n) return [];
  const totaalBudget = Math.max(n, Math.floor(Number(budget) || 0));
  let positief = gewichten.map((w) => Math.max(0, Number(w) || 0));
  let totaal = positief.reduce((a, b) => a + b, 0);
  if (totaal <= 0) { positief = positief.map(() => 1); totaal = n; }
  const ideaal = positief.map((w) => (totaalBudget * w) / totaal);
  const deel = ideaal.map((x) => Math.floor(x));
  const rest = totaalBudget - deel.reduce((a, b) => a + b, 0);
  const volgorde = [...Array(n).keys()].sort((a, b) =>
    ((ideaal[b] - deel[b]) - (ideaal[a] - deel[a])) || (positief[b] - positief[a]));
  for (let i = 0; i < rest; i += 1) deel[volgorde[i]] += 1;
  for (let i = 0; i < n; i += 1) {
    if (deel[i] === 0) {
      const gever = deel.reduce((best, v, j) => (v > deel[best] ? j : best), 0);
      deel[gever] -= 1;
      deel[i] = 1;
    }
  }
  return deel;
}

// Spiegel van _VERSIE / _VERSIE_STAART in de engine.
const VERSIE = [/v\s*intern/i, /v\s*\d+/i, /\bdef\b/i, /definitief/i];
const VERSIE_STAART = /\s*(v\s*\d+|v\s*intern|def|definitief).*$/i;

/** Zit er een feedbackronde tussen deze twee taken? Alleen tussen versies van hetzelfde werk. */
export function feedbackNodig(vorigeNaam, naam) {
  if (!vorigeNaam || !naam) return false;
  const a = String(vorigeNaam).toLowerCase();
  const b = String(naam).toLowerCase();
  const basisA = a.replace(VERSIE_STAART, '').trim();
  const basisB = b.replace(VERSIE_STAART, '').trim();
  if (!basisA || basisA !== basisB) return false;
  return VERSIE.some((p) => p.test(a)) && VERSIE.some((p) => p.test(b));
}

/** Aantal feedbackovergangen in een (gesorteerde) takenlijst van namen. */
export const aantalFeedbackRondes = (namen) =>
  namen.reduce((som, naam, i) => som + (i > 0 && feedbackNodig(namen[i - 1], naam) ? 1 : 0), 0);

/**
 * Taken op de tijdlijn leggen, in kolommen (werkdag-index vanaf de start).
 * Zelfde regels als leg_neer in de engine: eerst de planning-offset, dan per
 * taak eventueel feedbackdagen, dan de eigen verschuiving; de volgende taak
 * begint ná het einde van de vorige (een verschoven taak duwt de rest mee).
 */
export function legPlanningNeer(taken, { feedbackDagen = 0, planningOffset = 0, taakOffsets = {}, duurOverrides = {} } = {}) {
  let cursor = Math.max(0, planningOffset);
  const uit = [];
  taken.forEach((taak, i) => {
    const vorige = i > 0 ? taken[i - 1] : null;
    if (feedbackDagen > 0 && vorige && feedbackNodig(vorige.name, taak.name)) {
      uit.push({ id: `feedback-${i}`, name: 'Feedback', isFeedbackGap: true, startIdx: cursor, duration: feedbackDagen });
      cursor += feedbackDagen;
    }
    const taakOffset = Math.max(0, taakOffsets[taak.id] || 0);
    cursor += taakOffset;
    const baseDuration = Math.max(1, taak.days || 1);
    const duration = duurOverrides[taak.id] !== undefined ? Math.max(1, duurOverrides[taak.id]) : baseDuration;
    uit.push({ ...taak, startIdx: cursor, duration, baseDuration, taakOffset });
    cursor += duration;
  });
  return uit;
}

/**
 * Laatste dag van de laatste échte draaidag (4-laags filter). Pure datums
 * (EindDatum/StartDatum) eerst; anders de Outlook-tijden, waarbij een einde op
 * lokale middernacht exclusief is (een hele dag op 5 okt eindigt op 6 okt 00:00).
 */
export function laatsteDraaidagIso(draaidagen = []) {
  let laatste = null;
  (draaidagen || []).forEach((d) => {
    const f = d?.fields || d || {};
    if (!isActieveDraaidag(f)) return;
    let dag = String(f.EindDatum || f.StartDatum || '').slice(0, 10) || null;
    if (!dag) {
      const eind = f.EinddatumOutlook ? new Date(f.EinddatumOutlook) : null;
      const begin = f.StartdatumOutlook ? new Date(f.StartdatumOutlook) : null;
      if (eind && !Number.isNaN(eind.getTime())) {
        const e = new Date(eind);
        if (e.getHours() === 0 && e.getMinutes() === 0 && (!begin || begin < e)) e.setDate(e.getDate() - 1);
        dag = formatDateISO(e);
      } else if (begin && !Number.isNaN(begin.getTime())) {
        dag = formatDateISO(begin);
      }
    }
    if (dag && (!laatste || dag > laatste)) laatste = dag;
  });
  return laatste;
}

/**
 * Vroegste start van de postproductie: niet vóór vandaag en niet vóór de dag na
 * de laatste draaidag — monteren kan pas als er gedraaid is.
 */
export function vroegsteStart(draaidagen, vandaagIso = formatDateISO(new Date())) {
  const laatste = laatsteDraaidagIso(draaidagen);
  const naDraaidag = laatste ? volgendeWerkdag(laatste) : null;
  const vandaag = plusWerkdagen(vandaagIso, 0);
  if (naDraaidag && naDraaidag > vandaag) return { datum: naDraaidag, laatsteDraaidag: laatste };
  return { datum: vandaag, laatsteDraaidag: laatste };
}

/** Formaat kan een array, een komma-tekst ("16:9, 4:5") of leeg zijn. */
export function formatenLijst(waarde) {
  const ruw = Array.isArray(waarde) ? waarde : [waarde];
  const uit = [];
  ruw.forEach((w) => {
    const tekst = typeof w === 'object' && w !== null ? w.name : w;
    String(tekst || '').split(',').map((s) => s.trim()).filter(Boolean)
      .forEach((s) => { if (!uit.includes(s)) uit.push(s); });
  });
  return uit;
}

/** "wo 1 okt" */
export function korteDatum(iso) {
  const d = parseISODate(String(iso || '').slice(0, 10));
  if (!d) return '';
  return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }).replace('.', '');
}

/**
 * De hele planning zoals genereer_taken in de engine hem maakt (spiegel van
 * bereken_planning in ai-engine/api/frontdoor/tasks.py; gedeelde gevallen in
 * ai-engine/tests/fixtures/planning_gevallen.json).
 *
 * Wachtdagen eerst reserveren (planning-offset, feedbackrondes, verschuivingen,
 * de review-dag), de rest naar zwaarte verdelen, eigen duren erover, en loopt
 * het over een grens, de langste taak dag voor dag inkorten.
 *
 * Vaste dagen van de klant (`pinLimieten`: taakId → laatste werkdag-index waarop
 * die taak klaar moet zijn) knippen de planning in segmenten: start→v1,
 * v1+feedback→v2, …, laatste pin→deadline. Elk segment krijgt zijn eigen budget.
 * Past een segment niet, dan staat dat in `pinOverschrijdingen` — er wordt niets
 * stil verschoven. Zonder pins is dit exact de oude rekensom met één segment.
 *
 * taken: [{ id, name, zwaarte }] (gesorteerd, alleen actieve taken)
 * werkdagen: werkdagen van start t/m deadline (inclusief)
 */
export function berekenPlanning(taken, {
  werkdagen = 0, feedbackDagen = 0, planningOffset = 0, taakOffsets = {}, duurOverrides = {}, reviewDag = false,
  pinLimieten = {}, inkorten = true,
} = {}) {
  const n = taken.length;
  if (!n) return { duren: {}, posities: [], laatsteIdx: -1, overschrijding: 0, pinOverschrijdingen: {} };
  const fb = Math.max(0, parseInt(feedbackDagen, 10) || 0);
  const offset = Math.max(0, planningOffset || 0);
  const review = reviewDag ? 1 : 0;
  const W = werkdagen || 0;
  const deadlineIdx = W - 1;

  // Grens per taak: een klantpin, en voor de laatste taak ook de deadline.
  const limiet = taken.map((t, i) => {
    const pin = pinLimieten[t.id];
    let l = pin === undefined || pin === null ? null : Math.trunc(Number(pin));
    if (i === n - 1 && inkorten) l = l === null ? deadlineIdx - review : Math.min(l, deadlineIdx - review);
    return l;
  });
  const segmenten = [];
  let begin = 0;
  limiet.forEach((l, i) => {
    if (l !== null) { segmenten.push({ begin, eind: i, limiet: l }); begin = i + 1; }
  });
  if (begin < n) segmenten.push({ begin, eind: n - 1, limiet: null });

  // Wachtdagen binnen een segment (feedback vóór elke taak, verschuivingen).
  const wachtIn = (seg) => {
    let w = 0;
    for (let i = seg.begin; i <= seg.eind; i += 1) {
      if (i > 0 && fb > 0 && feedbackNodig(taken[i - 1].name, taken[i].name)) w += fb;
      w += Math.max(0, taakOffsets[taken[i].id] || 0);
    }
    return w;
  };
  // Achterwaarts: een latere pin begrenst ook de eerdere segmenten. Segment k
  // moet klaar zijn vóór segment k+1 minimaal nodig heeft (elke taak één dag
  // plus de wachtdagen). Anders at v1 alle ruimte op en haalde v2 zijn datum niet.
  for (let k = segmenten.length - 2; k >= 0; k -= 1) {
    const volgende = segmenten[k + 1];
    if (segmenten[k].limiet !== null && volgende.limiet !== null) {
      const nodig = (volgende.eind - volgende.begin + 1) + wachtIn(volgende);
      segmenten[k].limiet = Math.min(segmenten[k].limiet, volgende.limiet - nodig);
    }
  }

  const duren = new Array(n).fill(1);
  const leg = () => legPlanningNeer(
    taken.map((t, i) => ({ ...t, days: duren[i] })),
    { feedbackDagen: fb, planningOffset: offset, taakOffsets },
  );
  const taakEind = (pos, i) => {
    const p = pos.filter((x) => !x.isFeedbackGap)[i];
    return p.startIdx + p.duration - 1;
  };
  const pinOverschrijdingen = {};

  segmenten.forEach((seg) => {
    const cursor = seg.begin === 0 ? offset : taakEind(leg(), seg.begin - 1) + 1;
    const wacht = wachtIn(seg);
    const aantal = seg.eind - seg.begin + 1;
    const ruimte = seg.limiet === null ? W - review - cursor : seg.limiet - cursor + 1;
    const auto = verdeelDagen(taken.slice(seg.begin, seg.eind + 1).map((t) => t.zwaarte), Math.max(aantal, ruimte - wacht));
    for (let i = seg.begin; i <= seg.eind; i += 1) {
      const eigen = duurOverrides[taken[i].id];
      duren[i] = eigen !== undefined ? Math.max(1, parseInt(eigen, 10) || 1) : auto[i - seg.begin];
    }
    if (seg.limiet === null) return;
    let pos = leg();
    for (let poging = 0; taakEind(pos, seg.eind) > seg.limiet && poging < 500; poging += 1) {
      let langste = seg.begin;
      for (let i = seg.begin; i <= seg.eind; i += 1) if (duren[i] > duren[langste]) langste = i;
      if (duren[langste] <= 1) break;
      duren[langste] -= 1;
      pos = leg();
    }
  });

  // Te laat telt tegen de datum van de klant zelf — niet tegen de ingeperkte
  // grens (deadline of een latere pin): die meldt `overschrijding` al.
  const eindPos = leg();
  taken.forEach((t, i) => {
    const pin = pinLimieten[t.id];
    if (pin === undefined || pin === null) return;
    const over = taakEind(eindPos, i) - Math.trunc(Number(pin));
    if (over > 0) pinOverschrijdingen[t.id] = over;
  });

  const posities = leg();
  const laatsteIdx = Math.max(...posities.filter((p) => !p.isFeedbackGap).map((p) => p.startIdx + p.duration - 1)) + review;
  return {
    duren: Object.fromEntries(taken.map((t, i) => [t.id, duren[i]])),
    posities,
    laatsteIdx,
    overschrijding: Math.max(0, laatsteIdx - deadlineIdx),
    pinOverschrijdingen,
  };
}

/**
 * Klantpins ([{ taakId, datum, soort }]) → taakId → laatste werkdag-index.
 * Een feedbackpin hangt aan de versie vóór de feedback: die moet `feedbackDagen`
 * werkdagen eerder klaar zijn, zodat de klant zijn termijn heeft.
 * Spiegel van pin_limieten in de engine.
 */
export function pinLimieten(pins = [], { startIso, feedbackDagen = 0, taakIds = null } = {}) {
  const fb = Math.max(0, parseInt(feedbackDagen, 10) || 0);
  const uit = {};
  (pins || []).forEach((p) => {
    const datum = String(p?.datum || '').slice(0, 10);
    if (!p?.taakId || !/^\d{4}-\d{2}-\d{2}$/.test(datum)) return;
    if (taakIds && !taakIds.includes(p.taakId)) return;
    let idx = telWerkdagen(startIso, datum) - 1;
    if (p.soort === 'feedback') idx -= fb;
    uit[p.taakId] = uit[p.taakId] === undefined ? idx : Math.min(uit[p.taakId], idx);
  });
  return uit;
}
